import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  User,
  Users,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { cn } from '@/lib/utils';
import type { InstituteNoticeList } from '@/types/instituteNotice';
import type { StudentNoticeList } from '@/types/student';
import { NoticeFilterBar } from './NoticeFilterBar';
import { NoticeBannerIllustration, NoticeRowThumbnail } from './NoticeIllustrations';
import { NoticeStatCards } from './NoticeStatCards';
import { getNoticeTheme } from './noticeTheme';
import { fromClassNotices, fromInstituteNotices, type UnifiedNotice } from './unifiedNotice';

interface NoticesViewProps {
  classNotices: StudentNoticeList | null;
  instituteNotices: InstituteNoticeList | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  /** The line under the heading — who this board is about. */
  description?: string;
  /** What an empty board says, before any filter is applied. */
  emptyDescription?: string;
  pageSize?: number;
  showTeacherName?: boolean;
  hideHeader?: boolean;
}

function formatNoticeDateParts(dateStr: string) {
  if (!dateStr) return { date: '—', day: '' };
  const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return { date: dateStr, day: '' };
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return {
    date: `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`,
    day: days[d.getDay()],
  };
}

/**
 * Every notice one child's family is told — by their teachers and by the
 * office — as one interactive, filterable notices dashboard.
 */
export function NoticesView({
  classNotices,
  instituteNotices,
  isLoading,
  error,
  onRetry,
  description = 'Stay updated with important announcements, circulars and events from the school.',
  emptyDescription = 'When your teachers or the school office put something up, it appears here.',
  pageSize = 8,
  showTeacherName = true,
  hideHeader = false,
}: NoticesViewProps) {
  const schoolName = useInstituteBranding((state) => state.branding?.displayName);
  // Navigation State: Selected Notice for preview
  const [selectedNotice, setSelectedNotice] = useState<UnifiedNotice | null>(null);

  // Filter & Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('ALL');
  const [selectedSubject, setSelectedSubject] = useState('ALL');
  const [selectedSource, setSelectedSource] = useState('ALL');
  const [selectedDate, setSelectedDate] = useState('');
  const [sortBy, setSortBy] = useState<'NEWEST' | 'OLDEST' | 'TITLE_AZ'>('NEWEST');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);

  // Unified Notices
  const unifiedList = useMemo<UnifiedNotice[]>(
    () => [...fromClassNotices(classNotices), ...fromInstituteNotices(instituteNotices)],
    [classNotices, instituteNotices],
  );

  // Available subjects for filtering
  const availableSubjects = useMemo(() => {
    const subjects = new Set<string>();
    for (const notice of unifiedList) {
      if (notice.subject) subjects.add(notice.subject);
    }
    return [...subjects].sort((left, right) => left.localeCompare(right));
  }, [unifiedList]);

  // Summary Metrics Counts
  const totalCount = unifiedList.length;
  const instituteCount = unifiedList.filter((n) => n.source === 'INSTITUTE').length;
  const classCount = unifiedList.filter((n) => n.source === 'CLASS').length;
  const generalCount = unifiedList.filter((n) => n.noticeType === 'GENERAL').length;
  const eventsCount = unifiedList.filter((n) => n.noticeType === 'EVENT').length;
  const importantCount = unifiedList.filter((n) => n.noticeType === 'IMPORTANT').length;

  // Filtered and Sorted Notices
  const filteredNotices = useMemo(() => {
    return unifiedList
      .filter((n) => {
        // Search Filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const matchTitle = n.title.toLowerCase().includes(query);
          const matchBody = n.body.toLowerCase().includes(query);
          const matchSubject = n.subject?.toLowerCase().includes(query) ?? false;
          if (!matchTitle && !matchBody && !matchSubject) return false;
        }

        // Type Filter
        if (selectedType !== 'ALL' && n.noticeType !== selectedType) {
          return false;
        }

        // Subject Filter
        if (selectedSubject !== 'ALL' && n.subject !== selectedSubject) {
          return false;
        }

        // Source Filter
        if (selectedSource !== 'ALL' && n.source !== selectedSource) {
          return false;
        }

        // Date Filter
        if (selectedDate) {
          const rawDate = n.noticeDate || n.postedAt || '';
          const cleanDate = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate;
          if (cleanDate !== selectedDate) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
        if (sortBy === 'NEWEST') {
          const dateDiff = (b.noticeDate || '').localeCompare(a.noticeDate || '');
          if (dateDiff !== 0) return dateDiff;
          return (b.postedAt || '').localeCompare(a.postedAt || '');
        }
        if (sortBy === 'OLDEST') {
          const dateDiff = (a.noticeDate || '').localeCompare(b.noticeDate || '');
          if (dateDiff !== 0) return dateDiff;
          return (a.postedAt || '').localeCompare(b.postedAt || '');
        }
        if (sortBy === 'TITLE_AZ') return a.title.localeCompare(b.title);
        return 0;
      });
  }, [unifiedList, searchQuery, selectedType, selectedSubject, selectedSource, selectedDate, sortBy]);

  const hasFilters =
    searchQuery.trim() !== '' ||
    selectedType !== 'ALL' ||
    selectedSubject !== 'ALL' ||
    selectedSource !== 'ALL' ||
    selectedDate !== '';

  // Pagination calculations
  const totalFiltered = filteredNotices.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedNotices = filteredNotices.slice(startIndex, startIndex + pageSize);

  // Auto-select first notice on desktop if none selected
  useEffect(() => {
    if (!selectedNotice && paginatedNotices.length > 0 && typeof window !== 'undefined' && window.innerWidth >= 1024) {
      setSelectedNotice(paginatedNotices[0]);
    }
  }, [paginatedNotices, selectedNotice]);

  // Keep selected notice in sync if filters change
  useEffect(() => {
    if (selectedNotice && !filteredNotices.some((n) => n.uuid === selectedNotice.uuid)) {
      setSelectedNotice(filteredNotices[0] ?? null);
    }
  }, [filteredNotices, selectedNotice]);

  return (
    <div className="flex flex-col gap-5 max-w-full">
      {/* 1. Header Section */}
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Notices
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
              {description}
            </p>
          </div>
        </div>
      )}

      {/* 2. Top Summary Stat Cards */}
      {unifiedList.length > 0 && (
        <NoticeStatCards
          totalCount={totalCount}
          instituteCount={instituteCount}
          classCount={classCount}
          activeSource={selectedSource}
          onSelectSource={(source) => {
            setSelectedSource((prev) => (prev === source ? 'ALL' : source));
            setCurrentPage(1);
          }}
          generalCount={generalCount}
          eventsCount={eventsCount}
          importantCount={importantCount}
          activeFilter={selectedType}
          onSelectFilter={(type) => {
            setSelectedType((prev) => (prev === type ? 'ALL' : type));
            setCurrentPage(1);
          }}
        />
      )}

      {/* 3. Search & Filter Bar */}
      <NoticeFilterBar
        searchQuery={searchQuery}
        onSearchChange={(q) => {
          setSearchQuery(q);
          setCurrentPage(1);
        }}
        selectedSubject={selectedSubject}
        onSubjectChange={(s) => {
          setSelectedSubject(s);
          setCurrentPage(1);
        }}
        selectedSource={selectedSource}
        onSourceChange={(src) => {
          setSelectedSource(src);
          setCurrentPage(1);
        }}
        selectedDate={selectedDate}
        onDateChange={(d) => {
          setSelectedDate(d);
          setCurrentPage(1);
        }}
        sortBy={sortBy}
        onSortChange={setSortBy}
        availableSubjects={availableSubjects}
        onClearFilters={() => {
          setSelectedSubject('ALL');
          setSelectedSource('ALL');
          setSelectedDate('');
          setSortBy('NEWEST');
          setCurrentPage(1);
        }}
      />

      {/* 4. Loading State */}
      {isLoading && (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 p-6 shadow-xs">
          <SkeletonLines count={8} />
        </div>
      )}

      {/* 5. Error State */}
      {!isLoading && error && (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 p-6 flex flex-col items-start gap-3 shadow-xs">
          <Alert tone="critical" title="Could not load notices">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {/* 6. Main Split Content (Table on Left + Preview Card on Right) */}
      {!isLoading && !error && paginatedNotices.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          {/* LEFT COLUMN: Notices Table */}
          <div className={cn('flex flex-col min-w-0', selectedNotice ? 'lg:col-span-7 xl:col-span-7 2xl:col-span-8' : 'lg:col-span-12')}>
            <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs overflow-hidden flex flex-col justify-between">
              <div className="overflow-x-auto p-2 sm:p-2.5">
                <table className="w-full border-separate border-spacing-y-1 text-left text-xs">
                  <thead>
                    <tr className="text-slate-500 dark:text-slate-400 text-sm font-semibold">
                      <th className="py-2 px-2.5 text-center w-8">#</th>
                      <th className="py-2 px-2.5">Title</th>
                      <th className="py-2 px-2 text-center w-20">Type</th>
                      <th className="py-2 px-2 w-20">For</th>
                      <th className="py-2 px-2 w-24">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedNotices.map((notice, idx) => {
                      const isSelected = selectedNotice?.uuid === notice.uuid;
                      const badge = getNoticeTheme(notice.noticeType);
                      const dateParts = formatNoticeDateParts(notice.noticeDate);

                      return (
                        <tr
                          key={notice.uuid}
                          onClick={() => setSelectedNotice(notice)}
                          className="group cursor-pointer"
                        >
                          {/* 1. Index # */}
                          <td
                            className={cn(
                              'py-2 px-2.5 text-center font-bold text-xs transition-colors rounded-l-xl',
                              isSelected
                                ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border-y border-l border-indigo-500/70 dark:border-indigo-400/70 text-indigo-600 dark:text-indigo-400'
                                : 'border-y border-l border-transparent text-slate-400 dark:text-slate-500 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50',
                            )}
                          >
                            {startIndex + idx + 1}
                          </td>

                          {/* 2. Thumbnail + Title (Single Line) */}
                          <td
                            className={cn(
                              'py-2 px-2.5 transition-colors',
                              isSelected
                                ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border-y border-indigo-500/70 dark:border-indigo-400/70'
                                : 'border-y border-transparent group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50',
                            )}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <NoticeRowThumbnail title={notice.title} type={notice.noticeType} />
                              <div className="flex flex-col min-w-0">
                                <span
                                  className={cn(
                                    'text-xs sm:text-sm font-semibold truncate transition-colors',
                                    isSelected
                                      ? 'text-indigo-600 dark:text-indigo-400 font-bold'
                                      : 'text-slate-800 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
                                  )}
                                  title={notice.title}
                                >
                                  {notice.title}
                                </span>
                                {notice.source === 'CLASS' && notice.author && (
                                  <span className="text-2xs text-slate-500 dark:text-slate-400 font-medium truncate mt-0.5">
                                    By {notice.author}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* 3. Type Pill */}
                          <td
                            className={cn(
                              'py-2 px-2 text-center whitespace-nowrap transition-colors',
                              isSelected
                                ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border-y border-indigo-500/70 dark:border-indigo-400/70'
                                : 'border-y border-transparent group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50',
                            )}
                          >
                            <span className={cn('px-2 py-0.5 rounded-md text-2xs font-bold uppercase tracking-wider border', badge.badge)}>
                              {badge.label}
                            </span>
                          </td>

                          {/* 4. For */}
                          <td
                            className={cn(
                              'py-2 px-2 text-sm font-medium whitespace-nowrap truncate max-w-[100px] transition-colors',
                              isSelected
                                ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border-y border-indigo-500/70 dark:border-indigo-400/70 text-indigo-950 dark:text-indigo-200'
                                : 'border-y border-transparent text-slate-600 dark:text-slate-400 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50',
                            )}
                            title={notice.reaches || 'All Classes'}
                          >
                            {notice.reaches || 'All Classes'}
                          </td>

                          {/* 5. Date (Single Line) — the last column, so it closes the row's outline. */}
                          <td
                            className={cn(
                              'py-2 px-2 whitespace-nowrap transition-colors rounded-r-xl',
                              isSelected
                                ? 'bg-indigo-50/80 dark:bg-indigo-950/40 border-y border-r border-indigo-500/70 dark:border-indigo-400/70'
                                : 'border-y border-r border-transparent group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50',
                            )}
                          >
                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                              {dateParts.date}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Table Footer: Showing X of Y & Pagination Controls */}
              <div className="p-3 sm:p-3.5 border-t border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                <span className="font-semibold">
                  Showing {startIndex + 1} to {Math.min(startIndex + pageSize, totalFiltered)} of {totalFiltered} notices
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="h-7 w-7 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      className={cn(
                        'h-7 min-w-7 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer',
                        safePage === pageNum
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white',
                      )}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="h-7 w-7 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Selected Notice Preview Card */}
          {selectedNotice && (
            <div className="lg:col-span-5 xl:col-span-5 2xl:col-span-4 flex flex-col">
              <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-xs flex flex-col gap-3.5 sticky top-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white leading-snug">
                      {selectedNotice.title}
                    </h3>
                   
                  </div>

                </div>

                {/* Subheader Metadata */}
                {(() => {
                  const badge = getNoticeTheme(selectedNotice.noticeType);
                  const dateParts = formatNoticeDateParts(selectedNotice.noticeDate);
                  const isAdminNotice = selectedNotice.source === 'INSTITUTE';
                  const hideGeneralBadge = isAdminNotice && badge.label.toLowerCase() === 'general';

                  return (
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-slate-500 dark:text-slate-400">
                      {!hideGeneralBadge && (
                        <span className={cn('px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider border', badge.badge)}>
                          {badge.label}
                        </span>
                      )}
                      <span className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300 text-sm">
                        <Calendar className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                        {dateParts.date}{dateParts.day ? `, ${dateParts.day}` : ''}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300 text-sm">
                        <Users className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                        For: {selectedNotice.reaches || 'All Classes'}
                      </span>
                      {selectedNotice.subject && (
                        <span className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300 text-sm">
                          <BookOpen className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                          Subject: {selectedNotice.subject}
                        </span>
                      )}
                      {selectedNotice.source === 'CLASS' && selectedNotice.author && (
                        <span className="flex items-center gap-1 font-semibold text-indigo-600 dark:text-indigo-400 text-sm">
                          <User className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
                          Teacher: {selectedNotice.author}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Illustrated Themed Banner */}
                <NoticeBannerIllustration title={selectedNotice.title} type={selectedNotice.noticeType} />

                {/* Notice Body Content */}
                <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line font-medium mt-1">
                  {selectedNotice.body && selectedNotice.body.trim()
                    ? selectedNotice.body
                    : selectedNotice.source === 'INSTITUTE'
                      ? 'This is an official announcement from the school administration. Please note the circular details and follow the instructions accordingly.'
                      : selectedNotice.author
                        ? `This is an announcement from your class teacher (${selectedNotice.author})${selectedNotice.subject ? ` regarding ${selectedNotice.subject}` : ''}. Please review the note and follow the instructions accordingly.`
                        : `This is an announcement from your class teacher${selectedNotice.subject ? ` regarding ${selectedNotice.subject}` : ' regarding class activities and updates'}. Please review the note and follow the instructions accordingly.`}
                </div>

                {/* Sign-off */}
                <div className="mt-2 pt-3 border-t border-slate-200/80 dark:border-slate-800 flex flex-col text-sm font-semibold text-slate-500 dark:text-slate-400">
                  <span>Regards,</span>
                  {/* Who actually sent it — the teacher or the office member on
                      the record. Left out when the reader has chosen to hide
                      teacher names, rather than replaced with a stand-in. */}
                  {showTeacherName && selectedNotice.author && (
                    <span className="text-slate-800 dark:text-slate-200 font-bold mt-0.5">
                      {selectedNotice.author}
                    </span>
                  )}
                  {/* The reader's own school, from its settings — never a name
                      baked into the build (CLAUDE.md §4). */}
                  {schoolName && (
                    <span className="text-sm text-slate-400 dark:text-slate-500">{schoolName}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 7. Empty state */}
      {!isLoading && !error && paginatedNotices.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 backdrop-blur-sm p-10 text-center flex flex-col items-center justify-center shadow-xs">
          <div className="h-14 w-14 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-3 shadow-2xs">
            <Megaphone className="h-6 w-6" />
          </div>
          <h4 className="text-base font-bold text-slate-900 dark:text-white">
            {hasFilters ? 'No notices match those filters' : 'Nothing on the board yet'}
          </h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-medium max-w-md">
            {hasFilters
              ? 'Try a different search, or clear the filters to see everything you have been sent.'
              : emptyDescription}
          </p>
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSelectedType('ALL');
                setSelectedSubject('ALL');
                setSelectedSource('ALL');
                setSelectedDate('');
                setCurrentPage(1);
              }}
              className="mt-4 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-500 transition-colors cursor-pointer shadow-xs"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
